import {effectScope} from 'vue'
import {PropertyValueType} from '@toolify/server/src/services/DatabasePropertyService/types/PropertyValueType'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {
  useTextValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useTextValueTypeStrategy'
import {
  useNumberValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useNumberValueTypeStrategy'
import {
  useAutoIdValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useAutoIdValueTypeStrategy'
import {
  useCommentsValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useCommentsValueTypeStrategy'
import {
  useLocationValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useLocationValueTypeStrategy'
import {
  useLinkValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useLinkValueTypeStrategy'

import {
  usePhoneValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/usePhoneValueTypeStrategy'
import {
  useRatingValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useRatingValueTypeStrategy'
import {
  useMailValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useMailValueTypeStrategy'
import {
  useListValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useListValueTypeStrategy'
import {
  useFileValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useFileValueTypeStrategy'
import {
  useDateWithEntityValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useDateWithEntityValueTypeStrategy'
import {
  useDateValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useDateValueTypeStrategy'
import {
  useBooleanValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useBooleanValueTypeStrategy'
import {
  useTimeTrackerValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useTimeTrackerValueTypeStrategy'
import {
  usePeopleValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/usePeopleValueTypeStrategy'
import {
  useMarkdownValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useMarkdownValueTypeStrategy'
import {
  useStatusValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useStatusValueTypeStrategy'
import {
  useRelationValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useRelationValueTypeStrategy'
import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {
  useComputedValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useComputedValueTypeStrategy'
import {useChecklistValueTypeStrategy} from './useCheckListValueStrategy'
import {useVoteValueTypeStrategy} from './useVoteValueTypeStrategy'
import {useButtonValueTypeStrategy} from './useButtonValueTypeStrategy'
import {useWatchersValueTypeStrategy} from './useWatchersValueTypeStrategy'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {
  COMMON_GROUP_SORT_LABEL_KEYS,
  IGroupGranularityDescriptor,
  IGroupSortDescriptor,
} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {resolveNestedSubPropertyValue} from '@toolify/client/src/modules/databases/utils/resolveNestedSubPropertyValue'

const valueTypeStrategies = new Map<PropertyValueType, ReturnType<typeof createValueTypeStrategy>>()
const valueTypeStrategyScope = effectScope(true)

export function useValueTypeStrategy(valueType: PropertyValueType): ReturnType<typeof createValueTypeStrategy> {
  let strategy = valueTypeStrategies.get(valueType)
  if(!strategy) {
    strategy = valueTypeStrategyScope.run(() => createValueTypeStrategy(valueType))
    valueTypeStrategies.set(valueType, strategy)
  }
  return strategy
}

function createValueTypeStrategy (
  valueType: PropertyValueType,
) {
  const strategyComponentType = PropertyStrategyComponentType.Other

  const textValueTypeStrategy = useTextValueTypeStrategy
  const numberValueTypeStrategy = useNumberValueTypeStrategy
  const locationValueTypeStrategy = useLocationValueTypeStrategy
  const linkValueTypeStrategy = useLinkValueTypeStrategy
  const commentsValueTypeStrategy = useCommentsValueTypeStrategy 
  const autoIdValueTypeStrategy = useAutoIdValueTypeStrategy
  const booleanValueTypeStrategy = useBooleanValueTypeStrategy
  const dateValueTypeStrategy = useDateValueTypeStrategy    
  const dateWithEntityValueTypeStrategy = useDateWithEntityValueTypeStrategy
  const fileValueTypeStrategy = useFileValueTypeStrategy
  const listValueTypeStrategy = useListValueTypeStrategy
  const mailValueTypeStrategy = useMailValueTypeStrategy
  const phoneValueTypeStrategy = usePhoneValueTypeStrategy 
  const ratingValueTypeStrategy = useRatingValueTypeStrategy
  const timeTrackerValueTypeStrategy = useTimeTrackerValueTypeStrategy
  const markdownValueTypeStrategy = useMarkdownValueTypeStrategy
  const peopleValueTypeStrategy = usePeopleValueTypeStrategy
  const relationValueTypeStrategy = useRelationValueTypeStrategy
  const statusValueTypeStrategy = useStatusValueTypeStrategy
  const computedValueTypeStrategy = useComputedValueTypeStrategy
  const checklistValueStrategy = useChecklistValueTypeStrategy
  const voteValueTypeStrategy = useVoteValueTypeStrategy
  const buttonValueTypeStrategy = useButtonValueTypeStrategy
  const watchersValueTypeStrategy = useWatchersValueTypeStrategy

  const strategyMap = {
    [PropertyValueType.Text]: textValueTypeStrategy,
    [PropertyValueType.Number]: numberValueTypeStrategy,
    [PropertyValueType.Location]: locationValueTypeStrategy,
    [PropertyValueType.Link]: linkValueTypeStrategy,
    [PropertyValueType.Comments]: commentsValueTypeStrategy,
    [PropertyValueType.AutoId]: autoIdValueTypeStrategy,
    [PropertyValueType.Boolean]: booleanValueTypeStrategy,
    [PropertyValueType.Date]: dateValueTypeStrategy,
    [PropertyValueType.EntityWithDate]: dateWithEntityValueTypeStrategy,
    [PropertyValueType.File]: fileValueTypeStrategy,
    [PropertyValueType.Dropdown]: listValueTypeStrategy,
    [PropertyValueType.Mail]: mailValueTypeStrategy,
    [PropertyValueType.Phone]: phoneValueTypeStrategy,
    [PropertyValueType.Rating]: ratingValueTypeStrategy,
    [PropertyValueType.TimeTracker]: timeTrackerValueTypeStrategy,
    [PropertyValueType.Markdown]: markdownValueTypeStrategy,
    [PropertyValueType.Person]: peopleValueTypeStrategy,
    [PropertyValueType.Relation]: relationValueTypeStrategy,
    [PropertyValueType.Status]: statusValueTypeStrategy,
    [PropertyValueType.Checklist]: checklistValueStrategy,
    [PropertyValueType.Vote]: voteValueTypeStrategy,
    [PropertyValueType.Button]: buttonValueTypeStrategy,
    [PropertyValueType.Watchers]: watchersValueTypeStrategy,
    [PropertyValueType.Formula]: computedValueTypeStrategy,
  }

  const defaultSortGroupItems = (a: {fullValue: unknown}, b: {fullValue: unknown}): number => {
    const aValue = a.fullValue
    const bValue = b.fullValue
    
    if (!aValue && !bValue) return 0
    if (!aValue) return 1
    if (!bValue) return -1
    
    const aString = typeof aValue === 'string' ? aValue : String(aValue || '')
    const bString = typeof bValue === 'string' ? bValue : String(bValue || '')
    
    return aString.localeCompare(bString)
  }

  const defaultCanGroupByView = (_viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance): boolean => false

  const defaultGroupSort: IGroupSortDescriptor = {
    availableModes: [
      GroupSortModeType.ValueAsc,
      GroupSortModeType.ValueDesc,
      GroupSortModeType.CountDesc,
      GroupSortModeType.CountAsc,
    ],
    defaultMode: GroupSortModeType.ValueAsc,
    getLabelKey: (mode: GroupSortModeType): string => COMMON_GROUP_SORT_LABEL_KEYS[mode],
  }

  const compareTextsWithEmptiesLast = (aText: string, bText: string, direction: '1' | '-1'): number => {
    if(!aText && !bText) return 0
    if(!aText) return 1
    if(!bText) return -1
    const comparison = aText.localeCompare(bText)
    return direction === '1' ? comparison : -comparison
  }

  const defaultCompareItemValues = (a: unknown, b: unknown, direction: '1' | '-1'): number => {
    return compareTextsWithEmptiesLast(String(a ?? ''), String(b ?? ''), direction)
  }

  const defaultCanMoveItemToAnotherGroup = (property: IDatabasePropertyResponseInstance | null | undefined, _granularity?: GroupGranularityType | null): boolean => {
    return property?.canBeModified !== false
  }

  const defaultCanCreateItemInGroup = (property: IDatabasePropertyResponseInstance | null | undefined, _granularity?: GroupGranularityType | null, _groupValue?: unknown): boolean => {
    return property?.canBeModified !== false
  }

  const defaultStrategyShape = {
    canMoveItemToAnotherGroup: defaultCanMoveItemToAnotherGroup,
    canCreateItemInGroup: defaultCanCreateItemInGroup,
    canGroupByView: defaultCanGroupByView,
    fullValueToText: undefined,
    fullValueToColor: undefined,
    storageValueToInputValue: undefined,
    resolveSubPropertyValue: resolveNestedSubPropertyValue,
    fullValueToInputValue: undefined,
    strategyComponentType,
    socketCellValueToClientCellValue: undefined,
    getAvailableValues: undefined,
    getTitleText: undefined,
    getHrefFromValue: undefined,
    modifyProperty: undefined,
    getPossibleValueFilter: undefined,
    getFormulaValue: undefined,
    getDisplayValue: undefined,
    getValueAxisTickFormatter: undefined,
    buildValueAxisTicks: undefined,
    sortGroupItems: defaultSortGroupItems,
    compareItemValues: defaultCompareItemValues,
    updateValueBySingleValueGroupSwitch: undefined,
    updateFullValueBySingleValueGroupSwitch: undefined,
    searchToFilterQuery: undefined,
    getAdditionalPropertyIdsToFetch: undefined,
    groupGranularity: undefined as IGroupGranularityDescriptor | undefined,
    groupSort: defaultGroupSort,
  }

  if(!strategyMap[valueType]) {
    return defaultStrategyShape
  }
  const strategy = strategyMap[valueType]()

  const strategyFullValueToText = strategy.fullValueToText as
    | ((value: unknown, property?: unknown) => string | undefined)
    | undefined
  const strategyCompareItemValues = strategy.compareItemValues ?? ((a: unknown, b: unknown, direction: '1' | '-1', _property?: IDatabasePropertyResponseInstance): number => {
    const aText = strategyFullValueToText ? (strategyFullValueToText(a) ?? '') : String(a ?? '')
    const bText = strategyFullValueToText ? (strategyFullValueToText(b) ?? '') : String(b ?? '')
    return compareTextsWithEmptiesLast(aText, bText, direction)
  })

  return {
    ...defaultStrategyShape,
    ...strategy,
    canGroupByView: strategy.canGroupByView ?? defaultCanGroupByView,
    strategyComponentType: strategy.strategyComponentType || strategyComponentType,
    sortGroupItems: strategy.sortGroupItems || defaultSortGroupItems,
    compareItemValues: strategyCompareItemValues as (a: unknown, b: unknown, direction: '1' | '-1', property?: IDatabasePropertyResponseInstance) => number,
    groupSort: strategy.groupSort ?? defaultGroupSort,
  }
}
